// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~

import type { Moment } from 'moment';

import { UNIVERSES } from '../../constants/universes';
import type {
  Activity,
  Address,
  Appointment,
  BasketAttribute,
  BasketContent,
  BasketMode,
  ChildcareAllowanceParams,
  ChildrenParams,
  CleaningPrestation,
  CleaningPrestationOption,
  Customer,
  GiftCard,
  ProGender,
  Promotion,
  Schedule,
  UniverseReference,
} from '../../types/commons';
import type { ProChoice } from '../../types/customer';
import type { FetchArrayState, PayloadAction } from '../../types/state';
import { fetchInitialArrayState } from '../utils';

export const SET_ATTRIBUTE = 'BASKET_SET_ATTRIBUTE';

export type SetPaymentInfosParams = Partial<BasketState['paymentInfos']>;

export type BasketState = {
  activities: Activity[];
  address: Address | undefined;
  appointment: Appointment;
  appointmentDate: Moment | null;
  appointmentNear: boolean | null; // rename this with a "is" prefix ?
  basketContent: BasketContent;
  childcareAllowanceParams: ChildcareAllowanceParams;
  childcareHoursPerWeek?: number;
  childrenParams: ChildrenParams;
  cleaningPrestation?: CleaningPrestation;
  cleaningPrestationOptions?: CleaningPrestationOption[];
  closedTimeSlots: FetchArrayState<Moment> & {
    minAppointment: Moment | null;
    minAppointmentDelay: number | null;
    fetchedAt: Moment | null;
  };
  comments: string;
  customer: Customer | {};
  duration: number | null;
  endsAt: Moment | null;
  giftCard: GiftCard | {};
  hasPets?: boolean;
  includeHolidays: boolean; // rename this with a "is" prefix ?
  mode: BasketMode | null;
  paymentInfos: {
    stripePaymentMethodId?: string | null;
    stripePaymentIntentId?: string | null;
    stripeSetupIntentId?: string | null;
    preauthorizationSkipped?: boolean | null;
  };
  proChoices: ProChoice[];
  proGender: ProGender | null;
  promotion: Promotion;
  schedules: Schedule[];
  universe: UniverseReference;
  weekly_recurrence: number | null;
  zone: number | null;
};

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export const setBasketAttribute =
  <T>(attribute: BasketAttribute) =>
  (value: T) => ({
    type: SET_ATTRIBUTE,
    payload: { [attribute]: value },
  });

// When we use useDispatch it's hard to test the curryfied function so we have this new version that will replace the
// old one when we migrate from mapDispatchToProps to useDispatch
export const setBasketAttributeValue = <T>(attribute: BasketAttribute, value: T) => ({
  type: SET_ATTRIBUTE,
  payload: { [attribute]: value },
});

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~

export const initialState: BasketState = {
  activities: [],
  address: undefined,
  appointment: { primary: null, secondary: [] },
  appointmentDate: null,
  appointmentNear: null,
  basketContent: [],
  childcareAllowanceParams: {
    maritalStatus: undefined,
    incomeLevel: undefined,
  },
  childcareHoursPerWeek: undefined,
  childrenParams: {
    childrenCount: undefined,
    youngestChildAge: undefined,
    youngestBirthdate: undefined,
  },
  cleaningPrestation: undefined,
  cleaningPrestationOptions: undefined,
  closedTimeSlots: {
    ...fetchInitialArrayState,
    minAppointment: null,
    minAppointmentDelay: null,
    fetchedAt: null,
  },
  comments: '',
  customer: {},
  duration: null,
  endsAt: null,
  giftCard: {},
  hasPets: undefined,
  includeHolidays: false,
  mode: null,
  paymentInfos: {
    stripePaymentMethodId: null,
    stripePaymentIntentId: null,
    preauthorizationSkipped: null,
  },
  proChoices: [],
  proGender: null,
  promotion: {
    success: false,
    code: '',
    message: '',
    amount: 0,
    calculation_type: 'fixed',
  },
  schedules: [],
  universe: UNIVERSES.HAIRCUT,
  weekly_recurrence: null,
  zone: null,
};

export const onSetAttribute = (state: BasketState, action: PayloadAction<any>) => ({
  ...state,
  ...action.payload,
});

export const handlers = {
  [SET_ATTRIBUTE]: onSetAttribute,
};
